'use client';

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, AlertTriangle, Info, X, Sparkles } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastMessage {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

interface ToastContextType {
  toast: {
    success: (title: string, message?: string, duration?: number) => void;
    error: (title: string, message?: string, duration?: number) => void;
    warning: (title: string, message?: string, duration?: number) => void;
    info: (title: string, message?: string, duration?: number) => void;
    custom: (toast: Omit<ToastMessage, 'id'>) => void;
  };
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback((type: ToastType, title: string, message?: string, duration: number = 5000) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { id, type, title, message, duration }]);
  }, []);

  const toast = {
    success: (title: string, message?: string, duration?: number) => addToast('success', title, message, duration),
    error: (title: string, message?: string, duration?: number) => addToast('error', title, message, duration),
    warning: (title: string, message?: string, duration?: number) => addToast('warning', title, message, duration),
    info: (title: string, message?: string, duration?: number) => addToast('info', title, message, duration),
    custom: (newToast: Omit<ToastMessage, 'id'>) => {
      const id = Math.random().toString(36).substring(2, 9);
      setToasts((prev) => [...prev, { ...newToast, id, duration: newToast.duration || 5000 }]);
    }
  };

  return (
    <ToastContext.Provider value={{ toast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

const ToastContainer: React.FC<{ toasts: ToastMessage[]; removeToast: (id: string) => void }> = ({
  toasts,
  removeToast,
}) => {
  return (
    <div className="fixed top-6 inset-x-0 z-[100] flex flex-col items-center gap-3 pointer-events-none px-4 max-w-xl mx-auto">
      <AnimatePresence mode="popLayout">
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={() => removeToast(t.id)} />
        ))}
      </AnimatePresence>
    </div>
  );
};

const ToastItem: React.FC<{ toast: ToastMessage; onDismiss: () => void }> = ({ toast, onDismiss }) => {
  useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(() => {
        onDismiss();
      }, toast.duration);
      return () => clearTimeout(timer);
    }
  }, [toast.duration, onDismiss]);

  const getStyle = () => {
    switch (toast.type) {
      case 'success':
        return {
          bg: 'bg-white/95 dark:bg-[#1A2E26]/95 border-[#206A4F]/20 shadow-emerald-500/15',
          iconBg: 'bg-[#206A4F]/10 text-[#206A4F] dark:text-emerald-400',
          titleColor: 'text-[#206A4F] dark:text-emerald-300',
          progressBg: 'bg-[#206A4F]',
          icon: <CheckCircle2 className="w-6 h-6 shrink-0" />,
        };
      case 'error':
        return {
          bg: 'bg-white/95 dark:bg-rose-950/95 border-rose-500/20 shadow-rose-500/20',
          iconBg: 'bg-rose-100 dark:bg-rose-900/50 text-rose-600 dark:text-rose-400',
          titleColor: 'text-rose-700 dark:text-rose-300',
          progressBg: 'bg-rose-500',
          icon: <XCircle className="w-6 h-6 shrink-0" />,
        };
      case 'warning':
        return {
          bg: 'bg-white/95 dark:bg-amber-950/95 border-amber-500/20 shadow-amber-500/20',
          iconBg: 'bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400',
          titleColor: 'text-amber-800 dark:text-amber-300',
          progressBg: 'bg-amber-500',
          icon: <AlertTriangle className="w-6 h-6 shrink-0" />,
        };
      default:
        return {
          bg: 'bg-white/95 dark:bg-blue-950/95 border-blue-500/20 shadow-blue-500/15',
          iconBg: 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400',
          titleColor: 'text-blue-800 dark:text-blue-300',
          progressBg: 'bg-blue-500',
          icon: <Info className="w-6 h-6 shrink-0" />,
        };
    }
  };

  const style = getStyle();

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -40, scale: 0.85 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.85, y: -20 }}
      transition={{ type: 'spring', stiffness: 400, damping: 28 }}
      className={`pointer-events-auto relative w-full overflow-hidden rounded-[2.2rem] border backdrop-blur-xl shadow-2xl p-5 ${style.bg} transition-all duration-300`}
    >
      <div className="flex items-center gap-4 pr-2">
        {/* Icon container with bounce animation */}
        <motion.div
          initial={{ rotate: -20, scale: 0.5 }}
          animate={{ rotate: 0, scale: 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 15 }}
          className={`flex h-12 w-12 items-center justify-center rounded-2xl ${style.iconBg} shadow-inner`}
        >
          {style.icon}
        </motion.div>

        {/* Text Content */}
        <div className="flex-1 text-right rtl:text-right ltr:text-left">
          <div className="flex items-center gap-1.5 font-extrabold text-base md:text-lg">
            <span className={style.titleColor}>{toast.title}</span>
            {toast.type === 'success' && <Sparkles className="w-4 h-4 text-emerald-500 animate-pulse" />}
          </div>
          {toast.message && (
            <p className="mt-0.5 text-xs md:text-sm text-gray-600 dark:text-gray-300 leading-relaxed font-medium">
              {toast.message}
            </p>
          )}
        </div>

        {/* Dismiss Button */}
        <button
          onClick={onDismiss}
          className="rounded-full p-2 text-gray-400 hover:bg-black/5 dark:hover:bg-white/10 hover:text-gray-600 transition-colors shrink-0"
        >
          <X size={18} />
        </button>
      </div>

      {/* Animated timer bar at bottom */}
      {toast.duration && toast.duration > 0 && (
        <motion.div
          initial={{ width: '100%' }}
          animate={{ width: '0%' }}
          transition={{ duration: toast.duration / 1000, ease: 'linear' }}
          className={`absolute bottom-0 left-0 h-[3px] opacity-60 ${style.progressBg}`}
        />
      )}
    </motion.div>
  );
};
